import axios, { AxiosResponse } from "axios";
import { AuthBodyType } from "../types";
import { FluxoEntrada } from "../types/fluxo/types";
import { applyFirebaseTokenRetry } from './api';

export type PendenciaAgendamento = {
  id: string;
  idFluxo: string;
  versaoFluxo?: number;
  idChecklistCheckin: string;
  identificador: string;
  execucaoAnonimaId: string;
  inicioHistoricoAnonimo: number;
  fimHistoricoAnonimo: number;
  status: "aguardando_analise" | "agendado" | "recusado";
  criadoEm: number;
  dataAlteracao?: number;
  motivoRecusa?: string;
};

export type PaginaPendencias = {
  items: PendenciaAgendamento[];
  nextCursor?: string;
};

export type DetalhePendencia = {
  pendencia: Pick<
    PendenciaAgendamento,
    "id" | "status" | "inicioHistoricoAnonimo" | "motivoRecusa"
  >;
  versao: {
    idFluxo: string;
    versaoFluxo: number;
    nome: string;
  };
  state: Record<string, unknown> | null;
  checkin: {
    execucaoAnonimaId: string;
    idChecklistCheckin: string;
    inicioHistoricoAnonimo: number;
  };
};

class Fluxo {
  private api = applyFirebaseTokenRetry(axios.create({
    baseURL: `${process.env.REACT_APP_AXYMA_MICROSSERVICES_APIS_URL}/fluxo`,
  }));

  private config(body: AuthBodyType) {
    return {
      headers: {
        ...body,
      },
    };
  }

  create = async (
    body: AuthBodyType,
    fluxoModelo: {
      nome: string;
      entrada?: FluxoEntrada;
      fluxo?:
        | {
            checklistId: string;
            proximoChecklist?: string;
          }[]
        | null;
    },
  ) => {
    try {
      const data: AxiosResponse = await this.api.post(
        "/modelos",
        fluxoModelo,
        this.config(body),
      );
      return data;
    } catch (err) {
      throw err;
    }
  };

  delete = async (body: AuthBodyType, id: string) => {
    try {
      const data: AxiosResponse = await this.api.delete(
        `/modelos/${id}`,
        this.config(body),
      );
      return data;
    } catch (err) {
      throw err;
    }
  };

  update = async (
    body: AuthBodyType,
    id: string,
    fluxoModelo: {
      nome: string;
      entrada?: FluxoEntrada;
      fluxo?: {
        checklistId: string;
        proximoChecklist?: string;
      }[];
    },
  ) => {
    try {
      const data: AxiosResponse = await this.api.put(
        `/modelos/${id}`,
        fluxoModelo,
        this.config(body),
      );
      return data;
    } catch (err) {
      throw err;
    }
  };

  getAll = async (body: AuthBodyType) => {
    try {
      const data: AxiosResponse = await this.api.get(
        "/modelos",
        this.config(body),
      );
      return data;
    } catch (err) {
      throw err;
    }
  };

  atualizarUrlPublicaChecklist = async (
    body: AuthBodyType,
    idChecklist: string,
    urlPublica: { aliasEmpresa: string; aliasChecklist: string },
  ) => {
    const { data } = await this.api.put(
      `/modelos/checklists/${idChecklist}/url-publica`,
      urlPublica,
      this.config(body),
    );
    return data;
  };

  statesAction = async (
    body: AuthBodyType,
    action: string,
    payload: {
      idFluxo: string;
      idChecklist: string;
      idUnidade: string;
      identificador: string;
      dataPrevista: number;
    },
  ) => {
    try {
      const data: AxiosResponse = await this.api.post(
        "/states/action",
        { action, payload },
        this.config(body),
      );
      return data;
    } catch (err) {
      throw err;
    }
  };

  listarPendencias = async (
    body: AuthBodyType,
    params: {
      status?: PendenciaAgendamento["status"];
      limit?: number;
      cursor?: string;
    } = {},
  ) => {
    const response = await this.api.get<{
      success: boolean;
      data: PaginaPendencias;
    }>("/pendencias", { ...this.config(body), params });
    return response.data.data;
  };

  detalhePendencia = async (body: AuthBodyType, id: string) => {
    const response = await this.api.get<{
      success: boolean;
      data: DetalhePendencia;
    }>(`/pendencias/${encodeURIComponent(id)}`, this.config(body));
    return response.data.data;
  };

  resolverPendencia = async (
    body: AuthBodyType,
    id: string,
    agendamento: Record<string, unknown>,
  ) => {
    const response = await this.api.post(
      `/pendencias/${encodeURIComponent(id)}/resolver`,
      { agendamento },
      this.config(body),
    );
    return response.data.data;
  };

  recusarPendencia = async (body: AuthBodyType, id: string, motivo: string) => {
    const response = await this.api.post(
      `/pendencias/${encodeURIComponent(id)}/recusar`,
      { motivo },
      this.config(body),
    );
    return response.data.data;
  };
}

export default Fluxo;
